import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Skeleton,
  Alert,
  Button,
  Divider,
  Space,
  Tag,
  Empty,
  Table,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { Notice } from '../../components/common/Notice';
import { responseService } from '../../services/response.service';
import { ResponseData, ResponseAnswer } from '../../types/response.types';

// Phase 6 - US4: Participant Response Detail (T113)

const ParticipantResponseDetail = () => {
  const { responseHash } = useParams<{
    surveyId: string;
    responseHash: string;
  }>();
  const navigate = useNavigate();
  const [response, setResponse] = useState<ResponseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (responseHash) {
      loadResponse();
    }
  }, [responseHash]);

  const loadResponse = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await responseService.getResponseByHash(responseHash!);
      setResponse(data);
    } catch (err: any) {
      setError(
        err.response?.data?.error || 'Erro ao carregar sua resposta'
      );
    } finally {
      setLoading(false);
    }
  };

  const formatAnswerValue = (answer: ResponseAnswer) => {
    switch (answer.answerType) {
      case 'TEXT':
        return answer.textAnswer;
      case 'NUMBER':
        return answer.numericAnswer;
      case 'BOOLEAN':
        return answer.booleanAnswer ? 'Sim' : 'Não';
      case 'CHOICE':
        return answer.choiceAnswers?.[0] || '-';
      case 'CHOICES':
        return answer.choiceAnswers?.join(', ') || '-';
      case 'DATE':
        return answer.dateAnswer
          ? new Date(answer.dateAnswer).toLocaleDateString('pt-BR')
          : '-';
      default:
        return '-';
    }
  };

  const answerColumns = [
    {
      title: 'Pergunta',
      dataIndex: ['question', 'shortDescription'],
      key: 'question',
      width: '50%',
    },
    {
      title: 'Sua Resposta',
      key: 'answer',
      width: '50%',
      render: (_: any, record: ResponseAnswer) => formatAnswerValue(record),
    },
  ];

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (error || !response) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          Voltar
        </Button>
        <Alert
          message="Erro"
          description={error || 'Resposta não encontrada'}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/surveys')}
          className="mb-4"
        >
          Voltar para Pesquisas
        </Button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Sua Resposta
        </h1>
        <p className="text-gray-600">
          Visualize os detalhes da sua resposta à pesquisa
        </p>
      </div>

      {/* Survey Info Card */}
      <Card className="mb-6">
        <Descriptions
          title={
            <span className="flex items-center gap-2">
              <CheckCircleOutlined className="text-green-600" />
              {response.survey?.name}
            </span>
          }
          column={1}
        >
          <Descriptions.Item label="Hash da Resposta">
            <code className="bg-gray-100 px-3 py-1 rounded text-sm break-all">
              {response.responseHash}
            </code>
          </Descriptions.Item>
          <Descriptions.Item label="Data de Envio">
            <span className="flex items-center gap-2">
              <CalendarOutlined />
              {new Date(response.submittedAt).toLocaleDateString('pt-BR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </Descriptions.Item>
          {response.geographicScope && (
            <Descriptions.Item label="Abrangência">
              <Tag color="blue">
                {response.geographicScope === 'NATIONAL'
                  ? 'Nacional'
                  : response.geographicScope === 'STATE'
                  ? 'Estadual'
                  : 'Municipal'}
              </Tag>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Divider />

      {/* Answers */}
      <Card title={`Suas ${response.answers?.length || 0} Respostas`} className="mb-6">
        {!response.answers || response.answers.length === 0 ? (
          <Empty description="Nenhuma resposta registrada" />
        ) : (
          <Table
            columns={answerColumns}
            dataSource={response.answers}
            rowKey={(record) => record.question?.id || ''}
            pagination={false}
            size="small"
          />
        )}
      </Card>

      {/* Actions */}
      <Space className="w-full flex justify-center py-6">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/surveys')}
        >
          Voltar
        </Button>
        <Button
          type="primary"
          icon={<HomeOutlined />}
          onClick={() => navigate('/')}
        >
          Ir para Home
        </Button>
      </Space>

      {/* Info Footer */}
      <Notice
        message="Informação"
        description="Você pode usar o hash da sua resposta para consultar este formulário em qualquer momento. Guarde em local seguro se precisar fazer referência posterior."
        type="info"
        showIcon
        className="mt-6"
      />
    </div>
  );
};

export default ParticipantResponseDetail;
